import { FC, MouseEvent } from "react";
import { useIntl } from "react-intl";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";

/**
 * Categorical dot colors used to visually tie together the members of one
 * possible-duplicate group. Each detected group is assigned a color by index,
 * so two rows wearing the same color are flagged as part of the same group.
 */
export const DUPLICATE_DOT_COLORS = [
  "bg-sky-500",
  "bg-amber-500",
  "bg-emerald-500",
  "bg-violet-500",
  "bg-rose-500",
  "bg-cyan-500",
] as const;

export const getDuplicateDotColor = (colorIndex: number): string =>
  DUPLICATE_DOT_COLORS[colorIndex % DUPLICATE_DOT_COLORS.length];

/**
 * Per-row duplicate-marker data the list derives from the detected groups: the
 * group's color index (so siblings share a hue), the group size, and the member
 * ids needed to reopen the scoped review modal when the dot is clicked.
 */
export interface ChronologyDuplicateRowInfo {
  colorIndex: number;
  count: number;
  eventIds: string[];
}

interface ChronologyDuplicateDotProps {
  /** Group index; selects the dot color so siblings share a hue. */
  colorIndex: number;
  /** Number of events in the group, surfaced in the tooltip/label. */
  count: number;
  /**
   * Short row descriptor (date + detail) woven into the button's accessible
   * name. Without it, every dot in equally-sized groups exposes the same
   * "Review N possible duplicates" name, so screen-reader and voice-control
   * users can't tell which row they're opening.
   */
  rowLabel?: string;
  onClick: () => void;
  disabled?: boolean;
}

/**
 * A subtle, clickable colored dot that marks a chronology row as part of a
 * possible-duplicate group. Clicking it opens the review modal scoped to that
 * group. Rows in the same group share a color so reviewers can spot groupings
 * without opening anything.
 */
export const ChronologyDuplicateDot: FC<ChronologyDuplicateDotProps> = ({
  colorIndex,
  count,
  rowLabel,
  onClick,
  disabled = false,
}) => {
  const intl = useIntl();
  const tooltipLabel = intl.formatMessage(
    { id: "chronology.duplicates.dotLabel" },
    { count }
  );
  const ariaLabel =
    rowLabel && rowLabel.length > 0
      ? intl.formatMessage(
          { id: "chronology.duplicates.dotAriaLabel" },
          { count, row: rowLabel }
        )
      : tooltipLabel;

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onClick();
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={handleClick}
          disabled={disabled}
          aria-label={ariaLabel}
          className="ring-offset-background focus-visible:ring-ring inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full outline-hidden transition-opacity hover:opacity-70 focus-visible:ring-2 focus-visible:ring-offset-1 disabled:opacity-50"
        >
          <span
            className={cn(
              "h-2 w-2 rounded-full",
              getDuplicateDotColor(colorIndex)
            )}
          />
        </button>
      </TooltipTrigger>
      <TooltipContent>{tooltipLabel}</TooltipContent>
    </Tooltip>
  );
};
